import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import StarRating from './StarRating';
import { getProductPlaceholder } from '../utils/placeholder';

function QuickViewModal({ isOpen, onClose, product, onAddToCart, onToggleWishlist, isWishlisted, user, onOpenAuthModal, cart }) {
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [addedToCartCount, setAddedToCartCount] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const addToCartButtonRef = useRef(null);
  const autoSwipeIntervalRef = useRef(null);
  const pauseTimeoutRef = useRef(null);
  const imageContainerRef = useRef(null);
  const navigate = useNavigate();

  // Handle product images - filter by selected color if color_images mapping exists
  const productImages = useMemo(() => {
    const placeholderImage = product ? getProductPlaceholder(product.name, 600, 600) : '';
    if (!product) return [placeholderImage];
    
    // If color is selected and color_images mapping exists, use images for that color
    if (selectedColor) {
      // Check if color_images exists and has images for this color
      if (product.color_images && typeof product.color_images === 'object') {
        const colorSpecificImages = product.color_images[selectedColor];
        if (Array.isArray(colorSpecificImages) && colorSpecificImages.length > 0) {
          // Filter out any null/undefined/empty values
          const validImages = colorSpecificImages.filter(img => img && typeof img === 'string' && img.trim() !== '');
          if (validImages.length > 0) {
            return validImages;
          }
        }
      }
      
      // If color is selected but no color_images mapping, fall back to all images
      // This handles products that have colors but no color-image associations yet
    }
    
    // Otherwise, use all product images
    if (product.images && Array.isArray(product.images) && product.images.length > 0) {
      return product.images;
    }
    if (product.image && product.image.trim() !== '') {
      return [product.image];
    }
    return [placeholderImage];
  }, [product, selectedColor]);

  // Handle product videos - could be array or single string
  const productVideos = useMemo(() => {
    if (!product) return [];
    
    if (product.videos && Array.isArray(product.videos)) {
      return product.videos;
    }
    if (product.video && product.video.trim() !== '') {
      return [product.video];
    }
    return [];
  }, [product]);

  // Combine images and videos into a single media array (memoized)
  const allMedia = useMemo(() => [
    ...productImages.map(url => ({ type: 'image', url })),
    ...productVideos.map(url => ({ type: 'video', url }))
  ], [productImages, productVideos]);

  const hasMultipleMedia = allMedia.length > 1;

  // Prevent body scroll when modal is open - hooks must be called before any returns
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Reset media index when product changes or color selection changes
  useEffect(() => {
    setCurrentMediaIndex(0);
  }, [product?.id, selectedColor]);

  // Auto-swipe images
  useEffect(() => {
    // Clear any existing interval
    if (autoSwipeIntervalRef.current) {
      clearInterval(autoSwipeIntervalRef.current);
      autoSwipeIntervalRef.current = null;
    }

    // Only auto-swipe if modal is open, product exists, has multiple media, and not paused
    if (isOpen && product && allMedia.length > 1 && !isPaused) {
      const mediaLength = allMedia.length; // Capture length to avoid closure issues
      autoSwipeIntervalRef.current = setInterval(() => {
        setCurrentMediaIndex((prev) => {
          return (prev === mediaLength - 1 ? 0 : prev + 1);
        });
      }, 4000); // Change image every 4 seconds
    }

    // Cleanup interval on unmount or when dependencies change
    return () => {
      if (autoSwipeIntervalRef.current) {
        clearInterval(autoSwipeIntervalRef.current);
        autoSwipeIntervalRef.current = null;
      }
      if (pauseTimeoutRef.current) {
        clearTimeout(pauseTimeoutRef.current);
        pauseTimeoutRef.current = null;
      }
    };
  }, [isOpen, product, allMedia.length, isPaused]);

  // Calculate stock and sold based on selected color and size
  // Priority: variant_stock (color-size) > color_stock (color only) > general stock
  // Use useMemo to recalculate when selectedColor, selectedSize, or product changes
  // IMPORTANT: Hooks must be called before any early returns
  const stock = useMemo(() => {
    if (!product) return null;
    
    const productColors = product.colors && Array.isArray(product.colors) ? product.colors : [];
    const productSizes = product.sizes && Array.isArray(product.sizes) ? product.sizes : [];
    
    // If both color and size are selected, check variant_stock first
    if (selectedColor && selectedSize && productColors.length > 0 && productSizes.length > 0) {
      if (product.variant_stock && typeof product.variant_stock === 'object') {
        const variantKey = `${selectedColor}-${selectedSize}`;
        const variantStockValue = product.variant_stock[variantKey];
        if (variantStockValue !== undefined && variantStockValue !== null) {
          return parseInt(variantStockValue) || 0;
        }
      }
    }
    
    // If only color is selected (no sizes), check color_stock
    if (selectedColor && productColors.length > 0 && (!productSizes.length || !selectedSize)) {
      if (product.color_stock && typeof product.color_stock === 'object') {
        const colorStockValue = product.color_stock[selectedColor];
        if (colorStockValue !== undefined && colorStockValue !== null) {
          return parseInt(colorStockValue) || 0;
        }
      }
    }
    
    // Fallback to total stock if no color/size-specific stock
    return product.stock || 0;
  }, [selectedColor, selectedSize, product]);

  const sold = useMemo(() => {
    if (!product) return null;
    
    const productColors = product.colors && Array.isArray(product.colors) ? product.colors : [];
    const productSizes = product.sizes && Array.isArray(product.sizes) ? product.sizes : [];
    
    // If both color and size are selected, check variant_sold first
    if (selectedColor && selectedSize && productColors.length > 0 && productSizes.length > 0) {
      if (product.variant_sold && typeof product.variant_sold === 'object') {
        const variantKey = `${selectedColor}-${selectedSize}`;
        const variantSoldValue = product.variant_sold[variantKey];
        if (variantSoldValue !== undefined && variantSoldValue !== null) {
          return parseInt(variantSoldValue) || 0;
        }
      }
    }
    
    // If only color is selected (no sizes), check color_sold
    if (selectedColor && productColors.length > 0 && (!productSizes.length || !selectedSize)) {
      if (product.color_sold && typeof product.color_sold === 'object') {
        const colorSoldValue = product.color_sold[selectedColor];
        if (colorSoldValue !== undefined && colorSoldValue !== null) {
          return parseInt(colorSoldValue) || 0;
        }
      }
    }
    
    // Fallback to general sold if no color/size-specific sold
    return product.sold || 0;
  }, [selectedColor, selectedSize, product]);

  // Calculate remaining stock (stock - sold) - this is what we display
  const remainingStock = useMemo(() => {
    if (stock === null || sold === null) return null;
    const stockNum = parseInt(stock) || 0;
    const soldNum = parseInt(sold) || 0;
    return Math.max(0, stockNum - soldNum);
  }, [stock, sold]);

  // Reset selections when product changes
  useEffect(() => {
    if (product) {
      // Set first color as default if available
      const colors = product.colors && Array.isArray(product.colors) ? product.colors : [];
      setSelectedColor(colors.length > 0 ? colors[0] : null);
      
      // Set first size as default if available
      const sizes = product.sizes && Array.isArray(product.sizes) ? product.sizes : [];
      setSelectedSize(sizes.length > 0 ? sizes[0] : null);
      
      setQuantity(1);
      setAddedToCartCount(0); // Reset added count when product changes
    }
  }, [product]);

  // Update quantity when remaining stock changes (limit to available stock)
  useEffect(() => {
    if (remainingStock !== null && remainingStock !== undefined && quantity > remainingStock) {
      setQuantity(Math.max(1, remainingStock));
    }
  }, [remainingStock, quantity]);

  // Reset added count after 3 seconds
  useEffect(() => {
    if (addedToCartCount > 0) {
      const timer = setTimeout(() => {
        setAddedToCartCount(0);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [addedToCartCount]);

  // Keyboard navigation for media slider - must be before early return
  useEffect(() => {
    if (!isOpen || !product || allMedia.length <= 1) return;

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setCurrentMediaIndex((prev) => (prev === 0 ? allMedia.length - 1 : prev - 1));
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setCurrentMediaIndex((prev) => (prev === allMedia.length - 1 ? 0 : prev + 1));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, product, allMedia]);

  // Listen for fullscreen changes - must be before early return
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Early return after all hooks
  if (!isOpen || !product) return null;

  const displayPrice = parseFloat(product.price) || 0;
  const originalPrice = product.original_price ? parseFloat(product.original_price) : (product.originalPrice ? parseFloat(product.originalPrice) : null);
  const rating = product.rating || 4.5;
  const reviews = product.reviews || 0;
  
  const hasSale = originalPrice && originalPrice > displayPrice;
  const discountPercentage = hasSale 
    ? Math.round(((originalPrice - displayPrice) / originalPrice) * 100)
    : 0;
  const isNew = Boolean(product.is_new);
  const hasPromo = Boolean(product.is_promotional);
  
  const currentMedia = allMedia[currentMediaIndex] || allMedia[0] || null;

  const pauseAutoSwipe = () => {
    setIsPaused(true);
    // Clear any existing pause timeout
    if (pauseTimeoutRef.current) {
      clearTimeout(pauseTimeoutRef.current);
    }
    // Resume auto-swipe after 8 seconds
    pauseTimeoutRef.current = setTimeout(() => {
      setIsPaused(false);
      pauseTimeoutRef.current = null;
    }, 8000);
  };

  const goToPrevious = () => {
    pauseAutoSwipe();
    setCurrentMediaIndex((prev) => (prev === 0 ? allMedia.length - 1 : prev - 1));
  };

  const goToNext = () => {
    pauseAutoSwipe();
    setCurrentMediaIndex((prev) => (prev === allMedia.length - 1 ? 0 : prev + 1));
  };

  const goToMedia = (index) => {
    pauseAutoSwipe();
    setCurrentMediaIndex(index);
  };

  const formatSold = (count) => {
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K+`;
    }
    return `${count}+`;
  };

  const productColors = product?.colors && Array.isArray(product.colors) ? product.colors : [];
  const productSizes = product?.sizes && Array.isArray(product.sizes) ? product.sizes : [];
  
  // Helper function to get first image for a specific color
  const getColorThumbnail = (color) => {
    if (product?.color_images && typeof product.color_images === 'object' && product.color_images[color]) {
      const colorImages = product.color_images[color];
      if (Array.isArray(colorImages) && colorImages.length > 0) {
        return colorImages[0];
      }
    }
    // Fallback to first product image or placeholder
    if (product?.images && Array.isArray(product.images) && product.images.length > 0) {
      return product.images[0];
    }
    if (product?.image && product.image.trim() !== '') {
      return product.image;
    }
    return getProductPlaceholder(product?.name || '', 150, 150);
  };
  
  // Check if color/size selection is required
  const colorSelectionRequired = productColors.length > 0;
  const sizeSelectionRequired = productSizes.length > 0;
  
  // Calculate canAddToCart based on remaining stock for selected variant
  const canAddToCart = remainingStock !== null && remainingStock > 0 && 
    (!colorSelectionRequired || selectedColor !== null) && 
    (!sizeSelectionRequired || selectedSize !== null);

  const handleAddToCart = () => {
    if (!onAddToCart || !canAddToCart || !addToCartButtonRef.current) return;

    // Check if quantity exceeds available stock
    if (remainingStock !== null && remainingStock !== undefined && quantity > remainingStock) {
      // This shouldn't happen due to selector limits, but as a safety check
      setQuantity(Math.max(1, remainingStock));
      return;
    }

    // Get button position for flying animation
    const rect = addToCartButtonRef.current.getBoundingClientRect();
    const buttonCenterX = rect.left + rect.width / 2;
    const buttonCenterY = rect.top + rect.height / 2;
    
    // Include selected color and size in the product data (null if not applicable)
    const productWithSelection = {
      ...product,
      selectedColor: colorSelectionRequired ? selectedColor : null,
      selectedSize: sizeSelectionRequired ? selectedSize : null,
      quantity,
      remainingStock // Pass remaining stock for validation in App.jsx
    };
    
    // Trigger flying animation with button position
    onAddToCart(productWithSelection, { x: buttonCenterX, y: buttonCenterY });
    
    // Update added count
    setAddedToCartCount(prev => prev + quantity);
  };

  const handleToggleWishlist = () => {
    if (onToggleWishlist) {
      onToggleWishlist(product, !isWishlisted);
    }
  };

  const handleFullscreen = async () => {
    if (!imageContainerRef.current) return;

    try {
      if (!document.fullscreenElement) {
        await imageContainerRef.current.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (error) {
      console.error('Error toggling fullscreen:', error);
    }
  };

  const handleCheckout = () => {
    if (!canAddToCart) return;
    
    // Check if user is logged in
    if (!user) {
      // Open auth modal for user to login/register
      if (onOpenAuthModal) {
        onOpenAuthModal();
      }
      return;
    }
    
    // Create the product with selection (matching cart item format)
    const productWithSelection = {
      ...product,
      quantity: quantity,
      selectedColor: colorSelectionRequired ? selectedColor : null,
      selectedSize: sizeSelectionRequired ? selectedSize : null
    };
    
    // Add product to cart first (so it's in the cart state)
    if (onAddToCart && addToCartButtonRef.current) {
      const rect = addToCartButtonRef.current.getBoundingClientRect();
      const buttonCenterX = rect.left + rect.width / 2;
      const buttonCenterY = rect.top + rect.height / 2;
      
      onAddToCart(productWithSelection, { x: buttonCenterX, y: buttonCenterY });
    }
    
    // Close modal and navigate to checkout
    onClose();
    // Use setTimeout to ensure cart is updated before navigation
    // Don't pass selectedItems - let Checkout use the full cart prop instead
    setTimeout(() => {
      navigate('/checkout');
    }, 200); // Increased timeout to ensure cart state is updated
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[70] transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-[70] flex items-start justify-center pt-16 sm:pt-20 md:pt-24 px-1 sm:px-2 md:px-4 pb-2 sm:pb-4 md:pb-6 overflow-y-auto">
        <div 
          className="bg-white rounded-lg sm:rounded-xl md:rounded-2xl shadow-2xl max-w-full sm:max-w-2xl md:max-w-4xl lg:max-w-5xl w-full mb-2 sm:mb-4 md:mb-6 relative animate-modal-pop max-h-[85vh] sm:max-h-[90vh] md:max-h-[90vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-2 right-2 sm:top-3 sm:right-3 md:top-4 md:right-4 p-1.5 sm:p-2 md:p-2.5 bg-white hover:bg-gray-100 rounded-full transition-all shadow-lg hover:shadow-xl z-[80] border-2 border-gray-200 hover:border-gray-300"
            aria-label="Close modal"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div className="flex-1 overflow-y-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-3 md:gap-5 lg:gap-6 p-2 sm:p-3 md:p-5 lg:p-6">
            {/* Left Side - Media Slider */}
            <div 
              className="relative"
              onMouseEnter={() => setIsPaused(true)} // Pause auto-swipe on hover
              onMouseLeave={() => setIsPaused(false)} // Resume auto-swipe when mouse leaves
            >
              <div 
                ref={imageContainerRef}
                className={`w-full overflow-hidden bg-gray-100 rounded-lg sm:rounded-xl relative mb-1.5 sm:mb-2 md:mb-3 ${
                  currentMedia?.type === 'video' ? 'aspect-video' : 'aspect-[3/4]'
                }`}
              >
                {/* Main Media Display */}
                <div className="relative w-full h-full">
                  {currentMedia?.type === 'image' ? (
                    <img 
                      src={currentMedia.url} 
                      alt={`${product.name} view ${currentMediaIndex + 1}`}
                      className="w-full h-full object-contain transition-opacity duration-300"
                      onError={(e) => {
                        e.target.src = getProductPlaceholder(product.name, 600, 600);
                      }}
                    />
                  ) : currentMedia?.type === 'video' ? (
                    <video
                      src={currentMedia.url}
                      controls
                      className="w-full h-full object-contain bg-black"
                      preload="metadata"
                      onError={(e) => {
                        console.error('Video load error:', currentMedia.url);
                        const errorDiv = document.createElement('div');
                        errorDiv.className = 'absolute inset-0 flex items-center justify-center bg-gray-800 text-white p-2 sm:p-4 text-center text-xs sm:text-sm';
                        const errorText = document.createElement('p');
                        errorText.textContent = 'Video could not be loaded';
                        errorDiv.appendChild(errorText);
                        e.target.parentElement.appendChild(errorDiv);
                      }}
                    />
                  ) : (
                    <img 
                      src={getProductPlaceholder(product.name, 600, 600)} 
                      alt={`${product.name} placeholder`}
                      className="w-full h-full object-cover"
                    />
                  )}
                  
                  {/* Navigation Arrows */}
                  {hasMultipleMedia && (
                    <>
                      <button
                        onClick={goToPrevious}
                        className="absolute left-1 sm:left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full p-1 sm:p-1.5 md:p-2 shadow-lg transition-all hover:scale-110 z-20"
                        aria-label="Previous media"
                      >
                        <svg className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      <button
                        onClick={goToNext}
                        className="absolute right-1 sm:right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full p-1 sm:p-1.5 md:p-2 shadow-lg transition-all hover:scale-110 z-20"
                        aria-label="Next media"
                      >
                        <svg className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </>
                  )}

                  {/* Media Counter */}
                  {hasMultipleMedia && (
                    <div className="absolute top-2 right-2 sm:top-3 sm:right-3 bg-black/60 text-white text-[10px] sm:text-xs font-semibold px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full z-20">
                      {currentMediaIndex + 1} / {allMedia.length}
                    </div>
                  )}

                  {/* Fullscreen Button */}
                  {currentMedia?.type === 'image' && (
                    <button
                      onClick={handleFullscreen}
                      className="absolute bottom-2 right-2 sm:bottom-3 sm:right-3 bg-black/60 hover:bg-black/80 text-white rounded-md sm:rounded-lg p-1.5 sm:p-2 shadow-lg transition-all hover:scale-110 z-20"
                      aria-label="View fullscreen"
                    >
                      <svg className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              {/* Thumbnail Strip */}
              {hasMultipleMedia && allMedia.length > 0 && (
                <div className="flex gap-1.5 sm:gap-2 overflow-x-auto pb-1 sm:pb-2 scrollbar-hide">
                  {allMedia.map((media, index) => (
                    <button
                      key={index}
                      onClick={() => goToMedia(index)}
                      className={`relative flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-md sm:rounded-lg overflow-hidden border-2 transition-all ${
                        index === currentMediaIndex
                          ? 'border-primary ring-2 ring-primary/50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      aria-label={`View ${media.type} ${index + 1}`}
                    >
                      {media.type === 'image' ? (
                        <img
                          src={media.url}
                          alt={`${product.name} thumbnail ${index + 1}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = getProductPlaceholder(product.name, 150, 150);
                          }}
                        />
                      ) : (
                        <div className="relative w-full h-full bg-gray-800 flex items-center justify-center">
                          <video
                            src={media.url}
                            className="w-full h-full object-cover"
                            preload="metadata"
                            muted
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                            <svg className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          </div>
                          <div className="absolute top-0.5 right-0.5 sm:top-1 sm:right-1 bg-black/70 text-white text-[7px] sm:text-[8px] font-bold px-0.5 sm:px-1 py-0 sm:py-0.5 rounded">
                            VID
                          </div>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
              
              {/* Badges */}
              {(isNew || hasPromo) && (
                <div className="absolute top-2 left-2 sm:top-3 sm:left-3 flex flex-col gap-1.5 sm:gap-2 z-10">
                  {isNew && (
                    <span className="bg-green-500 text-white text-xs sm:text-sm font-semibold px-2 sm:px-3 py-0.5 sm:py-1 rounded-full shadow-lg">
                      New
                    </span>
                  )}
                  {hasPromo && (
                    <span className="bg-primary text-white text-xs sm:text-sm font-semibold px-2 sm:px-3 py-0.5 sm:py-1 rounded-full shadow-lg">
                      Promo
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Right Side - Details */}
            <div className="flex flex-col gap-2 sm:gap-3 md:gap-5">
              <div>
                <h2 className="text-lg sm:text-xl md:text-3xl font-bold text-gray-900 mb-0.5 sm:mb-1 md:mb-2 leading-tight">{product.name}</h2>
                {product.category && (
                  <p className="text-xs sm:text-sm text-gray-500 mb-1.5 sm:mb-2 md:mb-4">{product.category.name}</p>
                )}
              </div>

              {/* Price */}
              <div className="flex flex-wrap items-baseline gap-1.5 sm:gap-2 md:gap-3">
                <span className="text-xl sm:text-2xl md:text-4xl font-bold text-primary">GH¢{displayPrice.toFixed(2)}</span>
                {hasSale && (
                  <>
                    <span className="text-base sm:text-lg md:text-2xl text-gray-500 line-through">GH¢{originalPrice.toFixed(2)}</span>
                    <span className="text-xs sm:text-sm font-bold text-primary bg-primary/10 px-1 sm:px-1.5 md:px-2 py-0.5 sm:py-1 rounded">
                      Save GH¢{(originalPrice - displayPrice).toFixed(2)}
                    </span>
                  </>
                )}
              </div>

              {/* Rating & Reviews */}
              {(rating > 0 || reviews > 0) && (
                <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                  <StarRating rating={rating} />
                  <span className="text-xs sm:text-sm text-gray-600">
                    {rating.toFixed(1)} ({reviews} {reviews === 1 ? 'review' : 'reviews'})
                  </span>
                </div>
              )}

              {/* Product Info - Show when color is selected (and size if sizes exist) */}
              {selectedColor && (productSizes.length === 0 || selectedSize) && (
                <div className="flex flex-wrap gap-3 sm:gap-4 text-xs sm:text-sm text-gray-600">
                  {sold !== null && sold !== undefined && (
                    <div>
                      <span className="font-medium">Sold: </span>
                      <span>{formatSold(sold)}</span>
                    </div>
                  )}
                  {remainingStock !== null && remainingStock !== undefined && (
                    <div>
                      <span className="font-medium">Stock: </span>
                      <span className={remainingStock > 0 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                        {remainingStock > 0 ? `${remainingStock} available` : 'Out of stock'}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Color Selection */}
              {productColors.length > 0 && (
                <div>
                  <h3 className="text-xs sm:text-sm md:text-base font-bold text-gray-900 mb-1.5 sm:mb-2 md:mb-3">
                    Color: {selectedColor || productColors[0]}
                  </h3>
                  <div className="flex gap-1.5 sm:gap-2 md:gap-3 overflow-x-auto pb-1 sm:pb-2 scrollbar-hide">
                    {productColors.map((color, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedColor(color)}
                        className={`flex-shrink-0 flex flex-col items-center gap-1 sm:gap-1.5 md:gap-2 transition-all ${
                          selectedColor === color ? 'opacity-100' : 'opacity-70 hover:opacity-100'
                        }`}
                      >
                        <div className={`w-12 h-12 sm:w-16 sm:h-16 md:w-24 md:h-24 rounded-lg overflow-hidden border-2 transition-all ${
                          selectedColor === color
                            ? 'border-primary ring-2 ring-primary/50 shadow-lg'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}>
                          <img
                            src={getColorThumbnail(color)}
                            alt={color}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.src = getProductPlaceholder(product.name, 150, 150);
                            }}
                          />
                        </div>
                        <span className={`text-xs sm:text-sm font-medium text-center ${
                          selectedColor === color ? 'text-primary font-bold' : 'text-gray-700'
                        }`}>
                          {color}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Size Selection */}
              {productSizes.length > 0 && (
                <div>
                  <h3 className="text-xs sm:text-sm md:text-base font-bold text-gray-900 mb-1.5 sm:mb-2 md:mb-3">Size</h3>
                  <div className="flex flex-wrap gap-1.5 sm:gap-2 md:gap-3">
                    {productSizes.map((size, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedSize(size)}
                        className={`px-3 sm:px-4 md:px-6 py-1.5 sm:py-2 md:py-2.5 rounded-lg font-semibold text-xs sm:text-sm md:text-base transition-all border-2 ${
                          selectedSize === size
                            ? 'bg-primary text-white border-primary shadow-lg'
                            : 'bg-white text-gray-800 border-gray-300 hover:border-primary hover:text-primary'
                        }`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Quantity Selector */}
              <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
                <label className="text-xs sm:text-sm md:text-base font-semibold text-gray-900">Qty</label>
                <select
                  value={quantity}
                  onChange={(e) => {
                    const newQuantity = parseInt(e.target.value);
                    if (remainingStock !== null && remainingStock !== undefined && newQuantity > remainingStock) {
                      setQuantity(remainingStock);
                    } else {
                      setQuantity(newQuantity);
                    }
                  }}
                  disabled={remainingStock === null || remainingStock === 0}
                  className="px-2.5 sm:px-3 md:px-4 py-1.5 sm:py-2 md:py-2.5 rounded-lg border-2 border-gray-300 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all text-xs sm:text-sm md:text-base font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {remainingStock !== null && remainingStock !== undefined ? (
                    [...Array(Math.min(remainingStock, 10))].map((_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {i + 1}
                      </option>
                    ))
                  ) : (
                    [...Array(10)].map((_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {i + 1}
                      </option>
                    ))
                  )}
                </select>
                {remainingStock !== null && remainingStock !== undefined && remainingStock > 0 && (
                  <span className="text-xs text-gray-500">(Max: {remainingStock})</span>
                )}
              </div>

              {/* Description */}
              {product.description && (
                <div>
                  <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900 mb-0.5 sm:mb-1 md:mb-2">Description</h3>
                  <p className="text-xs sm:text-sm md:text-base text-gray-700 leading-relaxed line-clamp-3 sm:line-clamp-none">{product.description}</p>
                </div>
              )}

              {/* Selection Required Messages */}
              {(!canAddToCart && stock > 0) && (
                <div className="text-xs sm:text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  {colorSelectionRequired && !selectedColor && sizeSelectionRequired && !selectedSize && (
                    <span>Please select a color and size</span>
                  )}
                  {colorSelectionRequired && !selectedColor && (!sizeSelectionRequired || selectedSize) && (
                    <span>Please select a color</span>
                  )}
                  {sizeSelectionRequired && !selectedSize && (!colorSelectionRequired || selectedColor) && (
                    <span>Please select a size</span>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-col gap-1.5 sm:gap-2 md:gap-3 mt-auto pt-1.5 sm:pt-2 md:pt-4">
                <div className="flex flex-col sm:flex-row gap-1.5 sm:gap-2 md:gap-3">
                  <button
                    ref={addToCartButtonRef}
                    onClick={handleAddToCart}
                    disabled={!canAddToCart}
                    className={`flex-1 bg-primary text-white px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 rounded-lg font-semibold transition-all hover:bg-primary-dark hover:scale-105 shadow-lg flex items-center justify-center gap-1.5 sm:gap-2 text-xs sm:text-sm md:text-base ${
                      !canAddToCart ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    {addedToCartCount > 0 ? `${addedToCartCount} added` : 'Add to Cart'}
                  </button>
                  <button
                    onClick={handleToggleWishlist}
                    className={`px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 rounded-lg font-semibold transition-all border-2 flex items-center justify-center gap-1.5 sm:gap-2 text-xs sm:text-sm md:text-base ${
                      isWishlisted
                        ? 'bg-primary text-white border-primary'
                        : 'bg-white text-primary border-primary hover:bg-primary/10'
                    }`}
                  >
                    <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5" fill={isWishlisted ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                    <span className="hidden sm:inline">{isWishlisted ? 'Wishlisted' : 'Add to Wishlist'}</span>
                    <span className="sm:hidden">{isWishlisted ? 'Saved' : 'Save'}</span>
                  </button>
                </div>
                <button
                  onClick={handleCheckout}
                  disabled={!canAddToCart}
                  className={`w-full bg-gradient-to-r from-secondary via-secondary-dark to-secondary text-white px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 rounded-lg font-semibold transition-all hover:scale-105 shadow-lg flex items-center justify-center gap-1.5 sm:gap-2 text-xs sm:text-sm md:text-base ${
                    !canAddToCart ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-xl'
                  }`}
                >
                  <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  </svg>
                  Checkout
                </button>
              </div>
            </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default QuickViewModal;

